/**
 * Calendar Tools for the VoiceChat component
 * 
 * This file contains tools for time and calendar operations:
 * - getCurrentTime: Get the current local time and timezone
 * - getCalendarEvents: Retrieve events from the calendar store
 * - addCalendarEvents: Add new events to the calendar
 * - deleteCalendarEvents: Remove events from the calendar
 */
import { toolRegistry, createMessage, type OpenAIToolSchema } from './ToolRegistry';
import type { ToolContext, Message } from './ToolRegistry';
import { useCalendarStore } from '~/stores/useCalendarStore';
import { ToolName } from './ToolRegistry';

// Type for the calendar store instance
type CalendarStore = ReturnType<typeof useCalendarStore>;

// --------------------------------------
// Common Interfaces
// --------------------------------------

/**
 * Interface for calendar item from the store
 */
export interface CalendarItem {
  id?: string;
  description: string;
  starts_at: string;
  ends_at?: string;
  is_all_day: boolean;
}

/**
 * Interface for calendar event data (formatted for display)
 */
export interface CalendarEvent {
  id: string;
  description: string;
  date: string;
  time: string;
  starts_at: string;
  ends_at: string;
  is_all_day: boolean;
}

// --------------------------------------
// getCurrentTime Tool
// --------------------------------------

/**
 * Interface for time data response
 */
export interface TimeData {
  time: string;
  timezone: string;
  timestamp: string;
}

/**
 * Interface for time function result
 */
export interface TimeResult {
  time: string;
  timezone: string;
}

/**
 * Get the current time and timezone
 * @returns {TimeData} Object with time and timezone
 */
export function getCurrentTime(): TimeData {
  const now = new Date();
  const timeString = now.toLocaleTimeString();
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return {
    time: timeString,
    timezone: timezone,
    timestamp: now.toISOString()
  };
}

/**
 * Create a message object for displaying the current time
 * @returns {Message} Message object for the conversation
 */
export function createTimeMessage(): Message {
  const timeData = getCurrentTime();

  return createMessage(`The current time is ${timeData.time} (${timeData.timezone}).`);
}

/**
 * Format a time string for display
 * @param {Date} time - The time to format
 * @returns {string} Formatted time string
 */
export function formatTime(time: Date): string {
  return new Intl.DateTimeFormat('default', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).format(time);
}

// --------------------------------------
// getCalendarEvents Tool
// --------------------------------------

/**
 * Interface for the function response
 */
export interface CalendarEventsResponse {
  success: boolean;
  events: CalendarEvent[];
  count: number;
  timezone?: string;
  startDate?: string;
  endDate?: string;
}

/**
 * Format a calendar store item into a display-friendly event
 * @param {CalendarItem} item - The calendar item from the store
 * @param {string} timezone - The user's timezone
 * @returns {CalendarEvent} Formatted calendar event
 */
export function formatCalendarEvent(item: CalendarItem, timezone: string): CalendarEvent {
  const start = new Date(item.starts_at);
  const end = item.ends_at ? new Date(item.ends_at) : null;

  return {
    id: item.id || '',
    description: item.description,
    date: start.toLocaleDateString('en-US', { timeZone: timezone }),
    time: item.is_all_day ? 'All day' : `${start.toLocaleTimeString('en-US', { timeZone: timezone })} - ${end ? end.toLocaleTimeString('en-US', { timeZone: timezone }) : ''}`,
    starts_at: item.starts_at,
    ends_at: item.ends_at || '',
    is_all_day: item.is_all_day
  };
}

// --------------------------------------
// addCalendarEvents Tool
// --------------------------------------

/**
 * Interface for calendar event input data
 */
export interface CalendarEventInput {
  description: string;
  date: string;
  startTime?: string;
  endTime?: string;
  isAllDay?: boolean;
}

/**
 * Interface for calendar event arguments
 */
export interface CalendarEventArgs {
  events: CalendarEventInput[];
}

/**
 * Interface for a successfully created event
 */
export interface CreatedEvent {
  id: string;
  description: string;
  date: string;
  startTime?: string;
  endTime?: string;
  isAllDay?: boolean;
}

/**
 * Interface for a failed event creation
 */
export interface FailedEvent {
  event: CalendarEventInput;
  error: string;
}

/**
 * Interface for calendar event results
 */
export interface CalendarEventResults {
  created: CreatedEvent[];
  failed: FailedEvent[];
}

/**
 * Interface for the function response
 */
export interface CalendarEventResponse {
  success: boolean;
  events: CreatedEvent[];
  failed: FailedEvent[];
  totalCreated: number;
  totalFailed: number;
}

/**
 * Create a formatted message based on the event results
 * @param {CalendarEventResults} results - The results of the event creation
 * @returns {Message} A formatted message for the conversation
 */
export function createEventResultMessage(results: CalendarEventResults): Message {
  let messageText = '';

  if (results.created.length > 0) {
    if (results.created.length === 1) {
      const event = results.created[0];
      messageText = `I've added "${event.description}" to your calendar on ${event.date} at ${event.startTime || 'all day'}.`;
    } else {
      messageText = `I've added ${results.created.length} events to your calendar.`;
    }

    if (results.failed.length > 0) {
      messageText += ` However, ${results.failed.length} event${results.failed.length > 1 ? 's' : ''} could not be added.`;
    }
  } else {
    messageText = `I couldn't add any events to your calendar.`;
  }

  return createMessage(messageText);
}

/**
 * Parse date string into a Date object
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @param {string} timeStr - Optional time string in HH:MM format
 * @returns {Date} Parsed date
 * @throws {Error} If the date or time format is invalid
 */
export function parseDateTime(dateStr: string, timeStr: string): Date {
  // Validate input
  if (!dateStr || typeof dateStr !== 'string') {
    console.error(`Invalid date string: ${dateStr}`);
    throw new Error(`Invalid date format: ${dateStr}`);
  }

  if (!timeStr || typeof timeStr !== 'string') {
    console.error(`Invalid time string: ${timeStr}`);
    throw new Error(`Invalid time format: ${timeStr}`);
  }

  try {
    // Try to parse the combined date-time string
    const combinedStr = `${dateStr}T${timeStr}`;
    console.info(`Attempting to parse datetime: ${combinedStr}`);

    const date = new Date(combinedStr);

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.error(`Resulted in invalid date: ${combinedStr}`);
      throw new Error(`Invalid date-time combination: ${combinedStr}`);
    }

    console.info(`Successfully parsed date: ${date.toISOString()}`);
    return date;
  } catch (error) {
    console.error(`Error parsing datetime: ${dateStr} ${timeStr}`, error);
    throw new Error(`Failed to parse date and time: ${error instanceof Error ? error.message : String(error)}`);
  }
}

/**
 * Process a single calendar event
 * @param {CalendarEventInput} eventData - Event data to process
 * @param {Function} addToCalendar - Function to add the event to the calendar
 * @returns {Promise<CreatedEvent>} Created event data
 * @throws {Error} If validation fails or event creation fails
 */
export async function processCalendarEvent(
  eventData: CalendarEventInput,
  addToCalendar: (item: CalendarItem) => Promise<any>
): Promise<CreatedEvent> {
  // Validate required parameters
  if (!eventData.description) {
    throw new Error('Event description is required');
  }
  if (!eventData.date) {
    throw new Error('Event date is required');
  }
  if (!eventData.startTime && !eventData.isAllDay) {
    throw new Error('Event start time is required for non all-day events');
  }

  // Create start and end date objects
  let startDate = parseDateTime(eventData.date, eventData.startTime);
  let endDate: Date;

  if (eventData.isAllDay) {
    // For all-day events, set to midnight for start and end of day
    startDate.setHours(0, 0, 0, 0);
    endDate = new Date(startDate);
    endDate.setHours(23, 59, 59, 999);
  } else {
    // Parse end time if provided, otherwise default to 1 hour later
    if (eventData.endTime) {
      endDate = parseDateTime(eventData.date, eventData.endTime);
    } else {
      // Default to 1 hour event
      endDate = new Date(startDate.getTime() + 60 * 60 * 1000);
    }
  }

  // Create calendar item object
  const calendarItem: CalendarItem = {
    description: eventData.description,
    starts_at: startDate.toISOString(),
    ends_at: endDate.toISOString(),
    is_all_day: eventData.isAllDay === true
  };

  // Add to calendar and get result
  const result = await addToCalendar(calendarItem);

  // Return created event data
  return {
    id: result.id,
    description: result.description,
    date: eventData.date,
    startTime: eventData.startTime,
    endTime: eventData.endTime,
    isAllDay: eventData.isAllDay
  };
}

// --------------------------------------
// deleteCalendarEvents Tool
// --------------------------------------

/**
 * Interface for delete calendar events arguments
 */
export interface DeleteCalendarEventsArgs {
  eventIds: string[];
}

/**
 * Interface for a successfully deleted event
 */
export interface DeletedEvent {
  id: string;
}

/**
 * Interface for a failed event deletion
 */
export interface FailedDeletion {
  id: string;
  error: string;
}

/**
 * Interface for delete calendar events results
 */
export interface DeleteCalendarEventsResults {
  deleted: string[];
  failed: FailedDeletion[];
}

/**
 * Interface for the function response
 */
export interface DeleteCalendarEventsResponse {
  success: boolean;
  result: {
    deleted: string[];
    failed: FailedDeletion[];
    totalDeleted: number;
    totalFailed: number;
  };
}

/**
 * Create a formatted message based on the deletion results
 * @param {DeleteCalendarEventsResults} results - The results of the event deletion
 * @returns {Message} A formatted message for the conversation
 */
export function createDeletionResultMessage(results: DeleteCalendarEventsResults): Message {
  let messageText = '';

  if (results.deleted.length > 0) {
    messageText = `I've deleted ${results.deleted.length} event${results.deleted.length > 1 ? 's' : ''} from your calendar.`;
    if (results.failed.length > 0) {
      messageText += ` However, ${results.failed.length} event${results.failed.length > 1 ? 's' : ''} could not be deleted.`;
    }
  } else {
    messageText = `I couldn't delete any events from your calendar.`;
  }

  return createMessage(messageText);
}

// --------------------------------------
// OpenAI Tool Schemas
// --------------------------------------

/**
 * OpenAI schema for getCurrentTime tool
 */
const getCurrentTimeSchema: OpenAIToolSchema = {
  type: 'function',
  name: ToolName.GET_CURRENT_TIME,
  description: 'Get the current time',
  parameters: {
    type: 'object',
    properties: {},
    required: []
  }
};

/**
 * OpenAI schema for getCalendarEvents tool
 */
const getCalendarEventsSchema: OpenAIToolSchema = {
  type: 'function',
  name: ToolName.GET_CALENDAR_EVENTS,
  description: 'Get calendar events for a specific date range',
  parameters: {
    type: 'object',
    properties: {
      startDate: {
        type: 'string',
        description: 'Start date in YYYY-MM-DD format'
      },
      endDate: {
        type: 'string',
        description: 'End date in YYYY-MM-DD format'
      },
      timezone: {
        type: 'string',
        description: 'User\'s timezone (e.g., "America/New_York", "Europe/London")'
      }
    },
    required: ['timezone']
  }
};

/**
 * OpenAI schema for addCalendarEvents tool
 */
const addCalendarEventsSchema: OpenAIToolSchema = {
  type: 'function',
  name: ToolName.ADD_CALENDAR_EVENTS,
  description: 'Add one or more calendar events. For each event, you MUST specify the date, start time, and description accurately to create valid events. Do not call this function unless all required information is available for at least one event.',
  parameters: {
    type: 'object',
    properties: {
      events: {
        type: 'array',
        description: 'Array of calendar events to add',
        items: {
          type: 'object',
          properties: {
            description: {
              type: 'string',
              description: 'Description of the calendar event (e.g., "Meeting with John", "Doctor Appointment")'
            },
            date: {
              type: 'string',
              description: 'The date of the event in YYYY-MM-DD format',
              examples: ['2024-03-28', '2024-04-15']
            },
            startTime: {
              type: 'string',
              description: 'Start time in 24-hour format (HH:MM)',
              examples: ['09:30', '14:00', '18:45']
            },
            endTime: {
              type: 'string',
              description: 'End time in 24-hour format (HH:MM)',
              examples: ['10:30', '15:30', '20:00']
            },
            isAllDay: {
              type: 'boolean',
              description: 'Whether this is an all-day event'
            }
          },
          required: ['description', 'date']
        }
      }
    },
    required: ['events']
  }
};

/**
 * OpenAI schema for deleteCalendarEvents tool
 */
const deleteCalendarEventsSchema: OpenAIToolSchema = {
  type: 'function',
  name: ToolName.DELETE_CALENDAR_EVENTS,
  description: 'Delete one or more calendar events by their IDs. You MUST have previously retrieved the events and their ids for the events you want to delete.',
  parameters: {
    type: 'object',
    properties: {
      eventIds: {
        type: 'array',
        description: 'Array of calendar event IDs to delete',
        items: {
          type: 'string'
        }
      }
    },
    required: ['eventIds']
  }
};

// --------------------------------------
// Calendar Tools Registration
// --------------------------------------

/**
 * Register the getCurrentTime tool
 * @param context The tool context
 */
function registerGetCurrentTimeTool(): void {
  toolRegistry.register({
    name: ToolName.GET_CURRENT_TIME,
    openAISchema: getCurrentTimeSchema,
    execute: async (_, context: ToolContext) => {
      // Add time message to conversation immediately
      context.logger.info(`${ToolName.GET_CURRENT_TIME}: Tool execution started`);

      // Get the current time
      const timeData = getCurrentTime();
      context.logger.info(`${ToolName.GET_CURRENT_TIME}: Current time is ${timeData.time}`);

      // Add message to conversation
      context.conversation.push(createTimeMessage());
      context.logger.info(`${ToolName.GET_CURRENT_TIME}: Added time message to conversation`);

      // Return current time data
      return {
        time: timeData.time,
        timezone: timeData.timezone
      };
    },
    getContinuationInstructions: () => {
      return "Continue the conversation based on the current time function output.";
    }
  });
}

/**
 * Register the getCalendarEvents tool
 * @param calendarStore The calendar store instance
 */
function registerGetCalendarEventsTool(calendarStore: CalendarStore): void {
  toolRegistry.register({
    name: ToolName.GET_CALENDAR_EVENTS,
    openAISchema: getCalendarEventsSchema,
    execute: async (args: { startDate?: string, endDate?: string, timezone: string }, context: ToolContext) => {
      context.logger.info(`${ToolName.GET_CALENDAR_EVENTS}: Tool execution started`);

      if (!args.timezone) {
        throw new Error('Timezone parameter is required');
      }

      try {
        // Get events directly from the calendar store
        const allItems = calendarStore.calendarItems || [];
        context.logger.debug(`Retrieved ${allItems.length} total calendar items from store`);

        // Filter events by date range if provided
        let filteredItems = [...allItems];
        
        if (args.startDate) {
          const startDate = new Date(`${args.startDate}T00:00:00`);
          filteredItems = filteredItems.filter(item => new Date(item.starts_at) >= startDate);
        }
        
        if (args.endDate) {
          const endDate = new Date(`${args.endDate}T23:59:59`);
          filteredItems = filteredItems.filter(item => new Date(item.starts_at) <= endDate);
        }

        // Format events for response with timezone
        const formattedEvents = filteredItems.map(item => formatCalendarEvent(item, args.timezone));

        context.logger.info(`Successfully filtered and formatted ${formattedEvents.length} calendar items`);

        // Return response object
        return {
          success: true,
          events: formattedEvents,
          count: formattedEvents.length,
          timezone: args.timezone,
          startDate: args.startDate,
          endDate: args.endDate
        };
      } catch (error) {
        context.logger.error(`${ToolName.GET_CALENDAR_EVENTS}: Error retrieving calendar events:`, error);
        throw error;
      }
    },
    formatResult: (result) => null, // Don't add to conversation, just return the raw data
    getContinuationInstructions: () => {
      return "Use the calendar events data in JSON format to respond to the user's query. Do not create a textual summary unless specifically requested.";
    }
  });
}

/**
 * Register the addCalendarEvents tool
 * @param calendarStore The calendar store instance
 */
function registerAddCalendarEventsTool(calendarStore: CalendarStore): void {
  toolRegistry.register({
    name: ToolName.ADD_CALENDAR_EVENTS,
    openAISchema: addCalendarEventsSchema,
    execute: async (args: CalendarEventArgs, context: ToolContext) => {
      context.logger.info(`${ToolName.ADD_CALENDAR_EVENTS} Tool execution started`);

      // Validate required parameters
      if (!args.events || !Array.isArray(args.events) || args.events.length === 0) {
        context.logger.error(`${ToolName.ADD_CALENDAR_EVENTS}: Missing or empty events array in arguments`);
        throw new Error('At least one event must be provided');
      }

      // Log all events being processed
      context.logger.info(`${ToolName.ADD_CALENDAR_EVENTS}: Processing ${args.events.length} events`,
        args.events.map(e => ({ description: e.description, date: e.date, startTime: e.startTime })));

      // Track successful and failed events
      const results: CalendarEventResults = {
        created: [],
        failed: []
      };

      // Reference to the calendar store's add method
      const addToCalendar = async (item: CalendarItem) => {
        context.logger.debug(`${ToolName.ADD_CALENDAR_EVENTS}: Calling calendar store addCalendarItem`, item);
        // Use bind to ensure proper this context
        return calendarStore.addCalendarItem.bind(calendarStore)(item);
      };

      // Process each event
      for (const eventData of args.events) {
        try {
          context.logger.info(`${ToolName.ADD_CALENDAR_EVENTS}: Processing event "${eventData.description}" on ${eventData.date}`);
          const createdEvent = await processCalendarEvent(eventData, addToCalendar);
          results.created.push(createdEvent);
          context.logger.info(`${ToolName.ADD_CALENDAR_EVENTS}: Successfully added event: ${createdEvent.description}`);
        } catch (error) {
          context.logger.error(`${ToolName.ADD_CALENDAR_EVENTS}: Failed to add event: ${eventData.description}`, error);
          results.failed.push({
            event: eventData,
            error: error instanceof Error ? error.message : String(error)
          });
        }
      }

      // Add message to conversation
      context.conversation.push(createEventResultMessage(results));

      // Create response object
      return {
        success: results.created.length > 0,
        events: results.created,
        failed: results.failed,
        totalCreated: results.created.length,
        totalFailed: results.failed.length
      };
    },
    formatResult: () => null, // We add the message in execute
    getContinuationInstructions: () => {
      return "Acknowledge the events were added to the calendar and ask if the user would like to schedule any other events.";
    }
  });
}

/**
 * Register the deleteCalendarEvents tool
 * @param calendarStore The calendar store instance
 */
function registerDeleteCalendarEventsTool(calendarStore: CalendarStore): void {
  toolRegistry.register({
    name: ToolName.DELETE_CALENDAR_EVENTS,
    openAISchema: deleteCalendarEventsSchema,
    execute: async (args: DeleteCalendarEventsArgs, context: ToolContext) => {
      context.logger.info(`${ToolName.DELETE_CALENDAR_EVENTS}: Tool execution started`);

      // Validate required parameters
      if (!args.eventIds || !Array.isArray(args.eventIds) || args.eventIds.length === 0) {
        context.logger.error(`${ToolName.DELETE_CALENDAR_EVENTS}: Missing or empty eventIds array`);
        throw new Error('At least one event ID is required');
      }

      context.logger.info(`Event IDs to delete: ${JSON.stringify(args.eventIds)}`);

      // Keep track of deleted and failed events
      const results: DeleteCalendarEventsResults = {
        deleted: [],
        failed: []
      };

      // Delete each event
      for (const eventId of args.eventIds) {
        try {
          // Use the calendar store to delete
          await calendarStore.deleteCalendarItem.bind(calendarStore)(eventId);
          results.deleted.push(eventId);
          context.logger.info(`${ToolName.DELETE_CALENDAR_EVENTS}: Successfully deleted event with ID: ${eventId}`);
        } catch (error) {
          results.failed.push({
            id: eventId,
            error: error instanceof Error ? error.message : String(error)
          });
          context.logger.error(`${ToolName.DELETE_CALENDAR_EVENTS}: Failed to delete event with ID: ${eventId}`, error);
        }
      }

      // Add message to conversation
      context.conversation.push(createDeletionResultMessage(results));

      // Create response object
      return {
        success: results.deleted.length > 0,
        result: {
          deleted: results.deleted,
          failed: results.failed,
          totalDeleted: results.deleted.length,
          totalFailed: results.failed.length
        }
      };
    },
    formatResult: () => null, // We add the message in execute
    getContinuationInstructions: () => {
      return "Acknowledge the calendar event deletion and ask if the user needs anything else.";
    }
  });
}

/**
 * Register all calendar tools with the registry and configure them with the calendar store
 * Similar approach to how knowledge graph tools are registered and configured
 * 
 * @param calendarStore The calendar store to use for calendar operations
 * @param addEditTools Whether to add edit tools (add/delete)
 * @param includeTimeTool Whether to include the getCurrentTime tool
 * @returns void - No API needed as configuration is done directly
 */
export function registerCalendarTools(
  calendarStore: CalendarStore, 
  addEditTools: boolean = false,
  includeTimeTool: boolean = true
): void {
  // Register each tool individually
  if (includeTimeTool) {
    registerGetCurrentTimeTool();
  }
  registerGetCalendarEventsTool(calendarStore);
  if (addEditTools) {
    registerAddCalendarEventsTool(calendarStore);
    registerDeleteCalendarEventsTool(calendarStore);
  }

  console.info('Calendar tools registered and configured with calendar store');
}

/**
 * Get calendar events for today and the next 14 days
 * @param {CalendarStore | CalendarItem[]} calendarSource - Calendar store or array of calendar items
 * @param {string} timezone - User's timezone (e.g., "America/New_York")
 * @returns {CalendarEventsResponse} JSON object with formatted events
 */
export function getUpcomingCalendarEvents(
  timezone: string
) : string {
  const calendarStore = useCalendarStore();
  
  // Get all 
  const allItems = calendarStore.calendarItems;
  // Calculate date range (today to next 14 days)
  const today = new Date();
  const startDate = today.toISOString().split('T')[0]; // YYYY-MM-DD
  
  const endDay = new Date();
  endDay.setDate(today.getDate() + 14);
  const endDate = endDay.toISOString().split('T')[0]; // YYYY-MM-DD
  
  // Filter events by date range
  let filteredItems = [...allItems];
  
  // Apply start date filter
  const startDateTime = new Date(`${startDate}T00:00:00`);
  filteredItems = filteredItems.filter(item => new Date(item.starts_at) >= startDateTime);
  
  // Apply end date filter
  const endDateTime = new Date(`${endDate}T23:59:59`);
  filteredItems = filteredItems.filter(item => new Date(item.starts_at) <= endDateTime);
  
  // Format events for response with timezone
  const formattedEvents = filteredItems.map(item => formatCalendarEvent(item, timezone));

  const responseDict = {
    events: formattedEvents,
  }
  
  // Return response object
  return JSON.stringify(responseDict);
} 